/**
 * citationViewerStore — which citation is open in the source dialog.
 *
 * Kept outside the CitationList instances so the dialog (and a playing clip)
 * survives the hand-off from the streaming answer to the finished message,
 * which are two different CitationList instances. The dialog itself is
 * rendered once by <CitationViewerHost /> at app level.
 */

import { useEffect } from 'react';
import { create } from 'zustand';
import type { Citation } from './citations';

interface CitationViewerState {
  citation: Citation | null;
  /** Element that opened the dialog; focus goes back to it on close. */
  trigger: HTMLElement | null;
  openCitation: (citation: Citation, trigger?: HTMLElement | null) => void;
  closeCitation: () => void;
}

export const useCitationViewer = create<CitationViewerState>()((set) => ({
  citation: null,
  trigger: null,
  openCitation: (citation, trigger = null) => set({ citation, trigger }),
  closeCitation: () => set({ citation: null, trigger: null }),
}));

/**
 * Closes the source dialog when what it belongs to goes away: the chat panel
 * closes, another session is opened, or the route changes. Mounted once next
 * to <CitationViewerHost />.
 */
export function useCloseCitationOnLeave(chatOpen: boolean, sessionId: string | null, pathname: string): void {
  useEffect(() => {
    if (!chatOpen) useCitationViewer.getState().closeCitation();
  }, [chatOpen]);
  // Cleanup runs when the session or the route changes (and on unmount).
  useEffect(() => () => useCitationViewer.getState().closeCitation(), [sessionId, pathname]);
}
